import InsightsTooltip from 'components/ReusableComponents/Charts/Inslghts/InsightsTooltip'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import InfoBar from 'components/ReusableComponents/InfoBar'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { renderReferenceArea } from 'components/ReusableComponents/RestrictedDatesForCharts'
import Toggle from 'components/ReusableComponents/Toggle'
import renderYearSeperators from 'components/ReusableComponents/YearSeperatorsForCharts'
import { usePerformanceReportsDates } from 'features/Analytics'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import ConversionIcon from 'features/Analytics/Conversions/ConversionIcon'
import RevenueIcon from 'features/Analytics/Conversions/RevenueIcon'
import TotalNightsIcon from 'features/Analytics/Conversions/TotalNightsIcon'
import SegmentsChart from 'features/Analytics/SegmentsChart'
import ClickIcon from 'features/BillingManagement/Card/ClickIcon'
import Diagnose from 'features/Diagnose'
import { useInternalAdmin } from 'hooks'
import useAllHotelsToggle from 'hooks/useAllHotels'
import useRestrictedDates from 'hooks/useRestrictedDates'
import { PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import performanceReportsApi from 'services/api/performanceReports'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import { provideEndDate } from '../provideEndDate'
import CampaignConversionResults from '../SegmentsChart/CampaignConversionResults'
import styles from './Conversions.module.scss'
import { ReactComponent as LinkNewTabIcon } from './linkNewTab.svg'
import RegainedGuestsChart from './RegainedGuests'

const Conversions = () => {
  const { fromDate, setFromDate, toDate, setToDate } = usePerformanceReportsDates()

  const isInternalAdmin = useInternalAdmin()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain,
    setParentChain,
    isAllTime,
    setIsAllTime
  } = useAllHotelsToggle()

  const [campaignSummary, setCampaignSummary] = useState()
  const [dailyActivities, setDailyActivities] = useState()

  const [showGroupedCampaigns, setShowGroupedCampaigns] = useState(true)

  const [stats, setStats] = useState({})

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [showTotalBookings, setShowTotalBookings] = useState(false)

  const isUsingDefaultOperaReports = useSelector(
    s => s.hotelDetails.pms === PMSs.operadefault
  )

  const restrictedDates = useRestrictedDates(fromDate, toDate)

  const MAX_SEGMENTS_TO_SHOW = 5
  const [showingAllSegments, setShowingAllSegments] = useState(false)

  const activitiesArray =
    campaignSummary?.[showGroupedCampaigns ? 'activities' : 'activitiesDetails']
  const filteredActivitiesArray =
    activitiesArray?.length > 0 &&
    activitiesArray.filter(activity => activity.count !== 0)
  const remainingSegmentsCount = filteredActivitiesArray.length - MAX_SEGMENTS_TO_SHOW

  useEffect(() => {
    setIsLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    performanceReportsApi
      .conversion(
        {
          fromDate,
          toDate: provideEndDate(toDate),
          kind,
          parentChainCode: parentChain,
          isAllTheTime: isAllTime
        },
        abortController
      )
      .then(res => {
        setCampaignSummary(res.campaignSummary)
        setDailyActivities(res.dailyActivities)

        const {
          averageConversion,
          totalBookings,
          totalConvertedRevenue,
          totalClicks,
          directBookings
        } = res

        setStats({
          averageConversion,
          totalBookings,
          totalConvertedRevenue,
          totalClicks,
          directBookings
        })

        setIsErred(false)
        setIsLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setIsErred(true)
          setIsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain,
    isAllTime
  ])

  return (
    <div>
      {showTotalBookings.show ? (
        <CampaignConversionResults
          onClose={() => setShowTotalBookings({ show: false, key: null })}
          fromDate={fromDate}
          toDate={toDate}
          showWholeGroup={showWholeGroup}
          searchKey={showTotalBookings.key}
          kind={showWholeGroup ? 1 : showAllHotels ? 2 : 0}
          selectedParentChainFilter={parentChain}
        />
      ) : null}
      <AnalyticTabs
        showWholeGroup={showWholeGroup}
        setShowWholeGroup={setShowWholeGroup}
        setShowAllHotels={setShowAllHotels}
        showAllHotels={showAllHotels}
        setFromDate={setFromDate}
        setToDate={setToDate}
        fromDate={fromDate}
        toDate={toDate}
        parentChain={parentChain}
        setParentChain={setParentChain}
        isAllTime={isAllTime}
        setIsAllTime={setIsAllTime}
      />
      <DataIsReprocessingBar />
      <Diagnose />
      <InfoBar
        text={`The calculation of conversions is based on the reservation's ${
          isUsingDefaultOperaReports ? 'check-out date' : 'booking date'
        }.`}
        marginBottom={-5}
      />

      {isErred && (
        <div className={styles.noResults}>
          <p>Something went wrong while getting outreach data</p>
        </div>
      )}
      {!isErred && (
        <div className={styles.wrapper}>
          <div className={styles.row}>
            {!showAllHotels && (
              <RegainedGuestsChart
                kind={showWholeGroup ? 'WholeGroup' : 'CurrentSubsidiary'}
                fromDate={fromDate}
                toDate={toDate}
                hideBreakdownLinks={showWholeGroup}
              />
            )}
            <div className={styles.segmentsHolder}>
              <h3 className={styles.segmentsTitle}>Bookings by campaigns</h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <>
                  <div className={styles.segmentsHeader}>
                    <div
                      className={styles.segmentStat}
                      onClick={() => setShowTotalBookings({ show: true, key: false })}
                    >
                      <p className={styles.segmentsCountNumber}>
                        {formatNumberAsThousands(campaignSummary?.total)}
                      </p>
                      <div className={styles.countHolder}>
                        <p className={styles.segmentsCountLabel}>total bookings</p>
                        <LinkNewTabIcon
                          className={styles.regainedGuestsListLinkIcon}
                          fill="#0079ff"
                          width={16}
                          height={16}
                        />
                      </div>
                    </div>
                    <div className={styles.segmentStatDirect}>
                      <p className={styles.segmentsCountNumber}>
                        {formatNumberAsThousands(stats?.directBookings)}
                      </p>
                      <p className={styles.segmentsCountLabel}>direct bookings</p>
                    </div>
                  </div>
                  {campaignSummary && (
                    <div className={styles.segmentsContent}>
                      <SegmentsChart
                        hasConversionResults={true}
                        showWholeGroup={showWholeGroup}
                        showAllHotels={showAllHotels}
                        fromDate={fromDate}
                        toDate={toDate}
                        selectedParentChainFilter={parentChain}
                        barData={campaignSummary?.[
                          showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                        ]
                          .sort((a, b) => b.count - a.count)
                          .slice(0, MAX_SEGMENTS_TO_SHOW)}
                      />
                    </div>
                  )}
                  {(isInternalAdmin ? true : remainingSegmentsCount > 0) && (
                    <div className={styles.segmentsFooter}>
                      {isInternalAdmin && (
                        <Toggle
                          label="Group campaigns"
                          className={styles.groupCampaignsToggle}
                          checked={showGroupedCampaigns}
                          handleChange={() =>
                            setShowGroupedCampaigns(!showGroupedCampaigns)
                          }
                        />
                      )}
                      {remainingSegmentsCount > 0 && (
                        <button
                          className={styles.moreSegmentsButton}
                          onClick={() => setShowingAllSegments(true)}
                        >
                          <span className={styles.desktopText}>
                            Show {remainingSegmentsCount} more segments
                          </span>
                          <span className={styles.mobileText}>Show more</span>
                        </button>
                      )}
                    </div>
                  )}
                  {showingAllSegments && (
                    <ConfirmationDialog
                      title="Bookings by campaigns"
                      onClose={() => setShowingAllSegments(false)}
                      maxWidth={700}
                      contentPadding={0}
                      bottomBarDetail={
                        isInternalAdmin && (
                          <Toggle
                            label="Group campaigns"
                            className={styles.groupCampaignsToggleWithinDialog}
                            checked={showGroupedCampaigns}
                            handleChange={() =>
                              setShowGroupedCampaigns(!showGroupedCampaigns)
                            }
                          />
                        )
                      }
                    >
                      <div style={{ width: 650 }}>
                        {campaignSummary && (
                          <>
                            <SegmentsChart
                              hasConversionResults={true}
                              showAllHotels={showAllHotels}
                              showWholeGroup={showWholeGroup}
                              fromDate={fromDate}
                              toDate={toDate}
                              selectedParentChainFilter={parentChain}
                              barData={campaignSummary?.[
                                showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                              ].sort((a, b) => b.count - a.count)}
                            />
                          </>
                        )}
                      </div>
                    </ConfirmationDialog>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.statsHolder}>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <>
                  <div className={styles.stat}>
                    <RevenueIcon className={styles.statIcon} />
                    <div className={styles.statInner}>
                      <p className={styles.statValue}>
                        {Math.round(stats.totalConvertedRevenue).toLocaleString('en', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}
                      </p>
                      <p className={styles.statLabel}>Conversion revenue</p>
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <ConversionIcon className={styles.statIcon} />
                    <div className={styles.statInner}>
                      <p className={styles.statValue}>{stats.averageConversion}%</p>
                      <p className={styles.statLabel}>Average conversion</p>
                    </div>
                  </div>
                  {/* <div className={styles.warning}>
                    <p className={styles.warningTitle}>
                      <InfoIcon />
                      Problem update
                    </p>
                    <p className={styles.warningDesc}>
                      {' '}
                      We've found a problem in conversion % calculation and fix is on it's
                      way. We expect the data to be corrected by Friday March 17, 2023.
                      Thank you for your patience
                    </p>
                  </div> */}
                  <div className={styles.stat}>
                    <TotalNightsIcon className={styles.statIcon} />
                    <div className={styles.statInner}>
                      <p className={styles.statValue}>
                        {formatNumberAsThousands(stats.totalBookings)}
                      </p>
                      <p className={styles.statLabel}>Total bookings</p>
                    </div>
                  </div>
                  <div className={styles.stat}>
                    <ClickIcon className={styles.statIcon} />
                    <div className={styles.statInner}>
                      <p className={styles.statValue}>
                        {formatNumberAsThousands(stats.totalClicks)}
                      </p>
                      <p className={styles.statLabel}>Total clicks</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.marketingMessagesHolder}>
              <h3 className={styles.title}>Bookings &amp; revenue</h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    width={500}
                    height={300}
                    data={dailyActivities}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 15
                    }}
                  >
                    <CartesianGrid stroke="#F2F2F2" vertical={false} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      height={36}
                      payload={[
                        {
                          value: 'Bookings',
                          type: 'line',
                          color: '#4079F5',
                          dataKey: 'booking'
                        },
                        {
                          value: 'Revenue',
                          type: 'line',
                          color: '#66BB6A',
                          dataKey: 'revenue'
                        }
                      ]}
                    />
                    <XAxis
                      dataKey="date"
                      axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(0, 14)'
                      }}
                      tickFormatter={date => moment(date).format('MMM D')}
                      tickSize={0}
                      padding={{ top: 30, bottom: 20, right: 40 }}
                    />
                    <YAxis
                      tickSize={0}
                      axisLine={false}
                      yAxisId="left"
                      orientation="right"
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                    />
                    <YAxis
                      tickSize={0}
                      yAxisId="right"
                      tickFormatter={value => `$${value}`}
                      axisLine={false}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                    />
                    {renderReferenceArea(restrictedDates)}
                    {renderYearSeperators(dailyActivities)}
                    <Tooltip
                      content={
                        <InsightsTooltip
                          labelFormatter={date => moment(date).format('MMM D, YYYY')}
                        />
                      }
                      cursor={{
                        stroke: 'red',
                        strokeWidth: 0,
                        fill: '#dedede',
                        opacity: 0.22
                      }}
                    />
                    <Line
                      dataKey="clicks"
                      stroke="#10b981"
                      type="monotone"
                      yAxisId="right"
                      dot={false}
                      activeDot={{ r: 0 }}
                      strokeWidth={0}
                    />
                    <Line
                      dataKey="booking"
                      stroke="#4079F5"
                      type="monotone"
                      yAxisId="left"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line
                      dataKey="revenue"
                      stroke="#66BB6A"
                      type="monotone"
                      yAxisId="right"
                      dot={false}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Conversions
